import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  calculateCloudCoverage as sentinelCalcCloudCov,
  addCloudShadowMaskSentinel,
} from '../../algorithms/satellite/sentinel';
import { calculateCloudCoverage } from '../../algorithms/satellite/landsatToa';
import ee from '../../services/earth-engine';
import { sharpenBands } from '../../common/geeSharp/sharpeners.js';
import { stripSuffix } from '../../algorithms/utils';
import { Dswe } from '../../algorithms/utils/waterMask.js';
import { addSPM02 } from '../../algorithms/bathymetry/spm.js';
import { get_sur } from '../../common/siac/siacGetSur.js';
import { get_xap } from '../../common/siac/getXr.js';
const PreProcessingContext = createContext();

const MIN_SIZE = 200;

/**
 * @function PreProcessingColProvider
 * @description This component holds all functions used to process GEE images, was built based on Bathymetry module but can be reused in any module.
 * To use this provider, first create an image collection and store it in the CollectionRef reference. Once the collection is available, you can call
 * `cleanAndMaskImages` to apply all available masks (e.g., clouds, shadows, and particles) or invoke individual masking functions as needed.
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Child components that will receive the context.
 * @memberof module:Shared
 * @returns {JSX.Element} context provider
 */
export function PreProcessingColProvider({ children }) {
  const satelliteRef = useRef(null);
  const startDateRef = useRef(null);
  const roiRef = useRef(null);
  const collectionRef = useRef(null);
  const threshold = useRef(null);
  const waterMaskMethodRef = useRef('NDWI');

  /**
   * @function limitToRoi
   * @description Masks an image to the area of interest, the per-image counterpart of `image.clip(roi)`.
   * The pixels are the same either way; what differs is the footprint. A clipped image's footprint becomes the area itself, and
   * every operation over the collection's metadata then works on that geometry - the mosaic unites one footprint per tile of a
   * date. On a KML of a few vertices that is free; on the whole Babitonga bay (28 846 vertices, 109 islands) it ran out of memory
   * just listing the images of one period. A mask keeps each image's own tile footprint.
   * @param {ee.Image} image - Any image of the collection.
   * @memberof module:Shared
   * @returns {ee.Image} The image, masked outside the area of interest.
   */
  const limitToRoi = (image) => image.updateMask(ee.Image.constant(1).clip(roiRef.current).mask());

  /**
   * @function cleanAndMaskImages
   * @description runs all mask possibilities
   * @param {boolean} isToa - if the chosen mission is toa (top of atmosfere)
   * @param {string} syst_coord - system coordinate chosen
   * @param {object} satelliteInfo
   * @memberof module:Shared
   * @returns {void} this function result is apllied in collectionRef
   */
  const cleanAndMaskImages = async (isToa, syst_coord, satelliteInfo) => {
    await calculateCloudCoverageValues(isToa, syst_coord);
    await applyScaleFactors(satelliteInfo);
    await applyWaterMask(satelliteInfo);
    // await applyGlintCorrection(satelliteInfo);
    await applySPMMask(satelliteInfo);
    await addTideBand(satelliteInfo);
  };

  /**
   * @function generateCollection
   * @description Generates an image collection based on the selected satellite, region of interest (ROI), and date range.
   * Images with cloud coverage greater than 10% are excluded. For Sentinel-2
   * collections, image sharpening is automatically applied after collection generation.
   * @param {string} startDate - 'YYYY-MM-DD' build the collection from this date. If omitted, uses the value stored in `startDateRef.current`.
   * @param {string} endDate - 'YYYY-MM-DD' limit of the collection. If omitted, uses the current date.
   * @param {Array} missionsFilter - List of mission identifiers to include in the collection.
   * *Called by:
   * @see {handleChoose} - Function called after completing the AOI Chooser step
   * @memberof module:Shared
   * @returns {ee.ImageCollection} The generated and sorted image collection.
   * @throws {Error} Throws an error if collection generation fails.
   */
  const generateCollection = async (startDate = '', endDate = '', missionsFilter = ['LC08', 'LT05']) => {
    try {
      const type = window.sessionStorage.getItem('typeSatellite');
      const allMission = type === 'TOA' ? satelliteRef.current.missions : satelliteRef.current.surfaceReflectance;
      const cloudProperty = satelliteRef.current.name === 'Landsat' ? 'CLOUD_COVER' : 'CLOUDY_PIXEL_PERCENTAGE';
      if (endDate === '') endDate = ee.Date(Date.now());
      if (startDate === '') startDate = startDateRef.current;
      const missionFilter = allMission.filter((mission) => {
        return satelliteRef.current.name === 'Landsat' ? missionsFilter.includes(mission.shortname) : true;
      });
      const collectionsList = missionFilter.map((mission) => {
        let coll = ee.ImageCollection(mission.name).filterBounds(roiRef.current).filterDate(startDate, endDate);
        return coll.filter(ee.Filter.lt(cloudProperty, 10));
      });
      let combinedCollection = ee.ImageCollection(collectionsList[0]);
      for (let i = 1; i < collectionsList.length; i++) {
        combinedCollection = combinedCollection.merge(collectionsList[i]);
      }
      collectionRef.current = combinedCollection.sort(cloudProperty);
      if (satelliteRef.current.name === 'Sentinel-2') await sharpenCollection();
      return collectionRef.current;
    } catch (e) {
      console.error('generateCollection error:', e);
      throw e;
    }
  };

  /**
   * @function sharpenCollection
   * @description  Applies band sharpening to all images in the current collection.
   * For each image:
   * - Band B1 is sharpened using B2 as the reference band.
   * - Bands B5, B6, B7, B8A, B11, and B12 are sharpened using B8 as the reference band.
   *  The processed collection replaces the current value stored in `collectionRef.current`.
   * @see {sharpenBands} applies brovey with the indicated bands.
   * *Called by:
   * @see {@link handleChoose} - Function called after completing the AOI Chooser step
   * @returns {void}
   * @memberof module:Shared
   * @throws {Error} Throws an error if the sharpening process fails.
   */
  const sharpenCollection = () => {
    try {
      if (!collectionRef.current) return null;
      collectionRef.current = collectionRef.current.map((image) => {
        image = sharpenBands(image, 'B2', ['B1'], true);
        image = sharpenBands(image, 'B8', ['B5', 'B6', 'B7', 'B8A', 'B11', 'B12'], true);
        return image;
      });
    } catch (e) {
      console.error('sharpenCollection error:', e);
      throw e;
    }
  };

  /**
   * @function saveCloudCoverageValues
   * @description Saves satellite, starpret date, and region of interest (ROI) for later
   * @param {Object} satellite - Satellite object containing missions array with collection names
   * @param {String} startDate  - date to start getting cloud coverage
   * @param {Object} roi - google map earth polygon
   * @param {Number} thresholdValue - threshold to generate land mask
   * *Called by:
   * @see {handleChoose} - Function called after completing the AOI Chooser step
   * @memberof module:Shared
   * @returns {void}
   */
  const saveProcessingCollectionValues = useCallback(async (satellite, startDate, roi, thresholdValue) => {
    satelliteRef.current = satellite;
    startDateRef.current = startDate;
    roiRef.current = roi;
    threshold.current = thresholdValue;
  }, []);

  /**
   * @function calculateCloudCoverageValues
   * @description Calculates cloud coverage percentages for satellite images within a specified region of interest (ROI) and date range.
   * Processing varies according to the selected satellite
   * Landsat: Applies QA_PIXEL cloud masking when available.
   * Sentinel-2: Applies optical band scaling. Filters images using CLOUDY_PIXEL_PERCENTAGE metadata. Computes surface reflectance products.
   * @param {boolean} isToa - Indicates whether the collection is already a TOA (Top Of Atmosphere)
   * @param {string} syst_coord -Coordinate reference system (CRS) used during Sentinel-2 reprojection.
   * @memberof module:Shared
   * @returns {void} All resulting images are masked to the current ROI ({@link limitToRoi}) and stored in `collectionRef.current`.
   * @throws {Error} Throws an error if cloud coverage calculation or preprocessing fails.
   */
  const calculateCloudCoverageValues = useCallback(
    async (isToa = false, syst_coord = 'EPSG:4326') => {
      try {
        if (!isToa) {
          if (satelliteRef.current.name === 'Landsat') {
            collectionRef.current = collectionRef.current.map((image) => {
              const hasQABand = image.bandNames().contains('QA_PIXEL');
              if (hasQABand) {
                const qa = image.select('QA_PIXEL');
                const mask = qa
                  .bitwiseAnd(1 << 0)
                  .eq(0)
                  .and(qa.bitwiseAnd(1 << 1).eq(0))
                  .and(qa.bitwiseAnd(1 << 2).eq(0))
                  .and(qa.bitwiseAnd(1 << 3).eq(0))
                  .and(qa.bitwiseAnd(1 << 4).eq(0))
                  .and(qa.bitwiseAnd(1 << 5).eq(0));
                return image.updateMask(mask);
              }
              return image;
            });
          } else {
            collectionRef.current = collectionRef.current.map((image) => {
              const optical = image.select('B.*').multiply(0.0001);
              return limitToRoi(image.addBands(optical, null, true));
            });
            collectionRef.current = collectionRef.current.filter(ee.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', 50));
            return;
          }
        }
        if (satelliteRef.current.name === 'Landsat') {
          collectionRef.current = calculateCloudCoverage(
            satelliteRef.current,
            roiRef.current,
            startDateRef.current,
            collectionRef.current,
          );
          filterCloudROIByValue(0.5);
        } else {
          const thumbParams = {
            dimensions: 512,
            format: 'jpg',
            bands: ['B4', 'B3', 'B2'],
            min: 0,
            max: 3000,
            region: roiRef.current,
          };
          const filtered = collectionRef.current
            .filterBounds(roiRef.current)
            .filter(ee.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', 20));
          const after = collectionRef.current.first();
          collectionRef.current = filtered.map((image) => {
            const imageProj = image.reproject({ crs: 'EPSG:4326', scale: 10 });
            const sr = get_sur(imageProj.clip(roiRef.current), roiRef.current);
            const revImageProj = sr.reproject({ crs: syst_coord, scale: 10 });
            return ee.Image(
              revImageProj
                .select(['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B8A', 'B11', 'B12'])
                .copyProperties(image, image.propertyNames()),
            );
          });
          const before = collectionRef.current.first();
          collectionRef.current = sentinelCalcCloudCov(roiRef.current, startDateRef.current, collectionRef.current);
          collectionRef.current = addCloudShadowMaskSentinel(collectionRef.current);
        }
        collectionRef.current = collectionRef.current.map(limitToRoi);
      } catch (e) {
        console.error('Error calculating cloud coverage:', e);
        throw e;
      }
    },
    [satelliteRef, startDateRef, roiRef],
  );

  /**
   * @function applyScaleFactors
   * @description Applies Landsat surface reflectance scale factors to the optical bands of the current image collection.
   * For each mission, the corresponding images are identified using the mission shortname contained in the image system index. The optical
   * bands are then converted to reflectance values using the scale factor: reflectance = (pixelValue * 0.0000275) - 0.2
   * The processed images replace the original bands and the resulting collections are merged back into `collectionRef.current`.
   * @param {Array<Object>} missions - List of mission definitions. (shortname, bands)
   * @memberof module:Shared
   * @returns {void}
   * @throws {Error} Throws an error if the scale factor application fails.
   */
  const applyScaleFactors = (missions) => {
    try {
      if (satelliteRef.current.name !== 'Landsat') return;
      const processedCollections = missions.map((mission) => {
        const missionCollection = collectionRef.current.filter(
          ee.Filter.stringContains('system:index', mission.shortname),
        );
        return missionCollection.map((image) => {
          const bands = [
            mission.bands.blue,
            mission.bands.green,
            mission.bands.red,
            mission.bands.nir,
            mission.bands.swir,
            mission.bands.swir2,
            mission.bands?.aer,
          ].filter(Boolean);
          const optical = image.select(bands).multiply(0.0000275).add(-0.2).toFloat();
          return image.addBands(optical, null, true);
        });
      });
      collectionRef.current = processedCollections.reduce((acc, col) => acc.merge(col), ee.ImageCollection([]));
    } catch (e) {
      console.error('applyScaleFactors error:', e);
      throw e;
    }
  };

  /**
   * @function filterCloudROIByValue
   * @description Filters the current image collection by the percentage of cloud coverage calculated within the region of interest (ROI).
   * Only images whose `roiCloudPercentage` property is lower than the specified threshold are kept in the collection.
   * @param {number} percentage - Maximum allowed cloud coverage percentage within the ROI.
   * *Called by:
   * @see {@link calculateCloudCoverageValues}
   * @memberof module:Shared
   * @returns {void}
   * @throws {Error} Throws an error if the filtering operation fails.
   */
  const filterCloudROIByValue = (percentage) => {
    try {
      if (!collectionRef.current) return null;
      collectionRef.current = collectionRef.current.filter(ee.Filter.lt('roiCloudPercentage', percentage));
    } catch (e) {
      console.error('filterCloudROIByValue error:', e);
      throw e;
    }
  };

  /**
   * @function createMosaicByCommonId
   * @description perform mosaic to unite images with same ID
   * Returns the mosaic and leaves `collectionRef` as it was; a caller that wants the mosaic kept assigns it.
   * Pass the period's window rather than the whole collection: the join keeps every image of a group on its anchor, and over
   * the ten years of a Sentinel-2 collection clipped to a detailed KML area that ran out of memory, while the same join over
   * one period's images is cheap. Overwriting `collectionRef` with that window would lose the other dates, hence the return.
   * @param {ee.ImageCollection} optional collection in which to apply the mosaic, else it will be applied in the collectionRef
   * @example
   * same date/hour it only changes the last 2 characters witch define the tile position
   * 20171111T132231_20171111T132231_T22JGR
   * 20171111T132231_20171111T132231_T22JGS
   * @memberof module:Shared
   * @returns {ee.ImageCollection} collection with all images united by the same id
   * @throws {Error} Throws an error if the mosaic fails.
   */
  const createMosaicByCommonId = (collection = []) => {
    try {
      const selectedCollection = collection.length === 0 ? collectionRef.current : ee.ImageCollection(collection);
      const collectionWithTileId = selectedCollection.map((image) => {
        const id = ee.String(image.get('system:index'));
        const tileId = id.slice(0, id.length().subtract(2));
        return image.set('tile_id', tileId);
      });
      const uniqueTiles = collectionWithTileId.distinct(['tile_id']);
      const joinFilter = ee.Filter.equals({ leftField: 'tile_id', rightField: 'tile_id' });
      const saveAllJoin = ee.Join.saveAll({
        matchesKey: 'images_to_mosaic',
        ordering: 'system:time_start',
        ascending: true,
      });
      const joinedCollection = saveAllJoin.apply(uniqueTiles, collectionWithTileId, joinFilter);
      const mosaicked = joinedCollection.map((anchorImage) => {
        const tileId = anchorImage.get('tile_id');
        const imagesGroup = ee.ImageCollection.fromImages(anchorImage.get('images_to_mosaic'));
        const mosaicImage = imagesGroup.mosaic();
        return mosaicImage
          .copyProperties(anchorImage, anchorImage.propertyNames())
          .set('system:index', anchorImage.get('system:index'))
          .set('system:time_start', anchorImage.get('system:time_start'))
          .set('tile_id', tileId);
      });
      return ee.ImageCollection(mosaicked).map((img) => img.clip(roiRef.current));
    } catch (e) {
      console.error('createMosaicByCommonId error:', e);
      throw e;
    }
  };

  /**
   * @function applyWaterMask
   * @description apply water mask to all images of the collection. The primary water detection method is based on the Normalized Difference
   * Water Index (NDWI), calculated using the green and near-infrared (NIR) bands defined for each mission.
   * A binary water mask is generated by applying the threshold defined in `threshold.current`. Pixels classified as non-water are masked out.
   * For Sentinel collections, when `waterMaskMethodRef.current` is set to `DSWE`, an additional Dynamic Surface Water Extent (DSWE) classification
   * is applied. The resulting water mask is refined through morphological operations and small polygon filling before being combined with the NDWI mask.
   * The masked image is built from the original with `addBands` and `updateMask`, so it keeps the original's properties. This step, and the
   * SPM, tide and glint steps after it, no longer copy them again with `copyProperties(image, image.propertyNames())`: that copy made Earth
   * Engine materialise every image's clipped footprint, and on a KML area with thousands of vertices (the whole Babitonga bay has 28 846)
   * merely listing the images of a period ran out of memory ("User memory limit exceeded").
   * @param {Array<Object>} missions - List of mission definitions. (shortname, bands)
   * @memberof module:Shared
   * @returns {void}
   * @throws {Error} Throws an error if water mask generation fails.
   */
  const applyWaterMask = (missions) => {
    try {
      const processedCollections = missions.map((mission) => {
        const missionCollection =
          satelliteRef.current.name === 'Landsat'
            ? collectionRef.current.filter(ee.Filter.stringContains('system:index', mission.shortname))
            : collectionRef.current;
        return missionCollection.map((image) => {
          const ndwi = image.normalizedDifference([mission.bands.green, mission.bands.nir]).rename('ndwi');
          const imageNDWI = image.addBands(ndwi, null, true);
          const waterMask = imageNDWI.select('ndwi').gte(Number(threshold.current)).rename('waterMask_final');
          const imageWithMask = imageNDWI.addBands(waterMask, null, true);
          return imageWithMask.updateMask(waterMask);
        });
      });
      let newCollection = processedCollections.reduce((acc, col) => acc.merge(col), ee.ImageCollection([]));
      if (waterMaskMethodRef.current === 'DSWE' && satelliteRef.current.name !== 'Landsat') {
        newCollection = newCollection.map((image) => {
          let dswe_image = Dswe(image);
          let waterMask = dswe_image.eq(1).or(dswe_image.eq(2)).rename('waterMask');
          let dilatedWaterMask = waterMask.focal_max(1).focal_min(1);
          let smallPolygons = dilatedWaterMask
            .not()
            .selfMask()
            .reduceToVectors({
              geometry: roiRef.current,
              scale: 10,
              tileScale: 16,
              maxPixels: 1e6,
              eightConnected: true,
            })
            .filter(ee.Filter.lte('count', MIN_SIZE));
          let filledWaterMask = dilatedWaterMask.paint(smallPolygons, 1).rename('filledWaterMask');
          let maskedImage = image.updateMask(filledWaterMask);
          image = maskedImage.addBands([dswe_image, filledWaterMask], null, true);
          return image.updateMask(image.select('waterMask_final').gte(Number(threshold.current)));
        });
      }
      collectionRef.current = newCollection;
    } catch (e) {
      console.error('applyWaterMask error:', e);
      throw e;
    }
  };

  /**
   * @function applyGlintCorrection
   * @description Applies a Hedley et al. (2005) style sun-glint correction to the visible bands
   * (blue/green/red), using the NIR band as a glint proxy and the current water mask
   * (`waterMask_final`, set by `applyWaterMask`) as the reference region.
   * 1. Finds `NIR_min`, the lowest NIR value observed over water pixels in the ROI (the pixel
   *    with the least glint).
   * 2. For each visible band, fits a linear regression against NIR over water pixels
   *    (`ee.Reducer.linearFit()`, x = NIR, y = visible band) to get the glint slope.
   * 3. Subtracts the glint contribution: `corrected = band - slope * (NIR - NIR_min)`.
   * Acts as a fallback for imagery that doesn't go through SIAC, which is expected to already
   * handle glint as part of its atmospheric correction.
   * @param {Array<Object>} missions - List of mission definitions. (shortname, bands, opticalResolution)
   * @see {@link applyWaterMask} - must run first so `waterMask_final` exists.
   * @memberof module:Shared
   * @returns {void}
   * @throws {Error} Throws an error if the glint correction fails.
   */
  const applyGlintCorrection = (missions) => {
    try {
      const processedCollections = missions.map((mission) => {
        const missionCollection =
          satelliteRef.current.name === 'Landsat'
            ? collectionRef.current.filter(ee.Filter.stringContains('system:index', mission.shortname))
            : collectionRef.current;
        const scale = mission.opticalResolution;
        const visibleBands = [mission.bands.blue, mission.bands.green, mission.bands.red];

        const correctedMissionCollection = missionCollection.map((image) => {
          const nir = image.select(mission.bands.nir);
          const waterMask = image.select('waterMask_final');

          const nirMin = ee.Number(
            nir
              .updateMask(waterMask)
              .reduceRegion({
                reducer: ee.Reducer.min(),
                geometry: roiRef.current,
                scale,
                bestEffort: true,
                tileScale: 8,
              })
              .get(mission.bands.nir),
          );

          const correctedBands = visibleBands.map((bandName) => {
            const band = image.select(bandName);
            const stack = ee.Image.cat([nir.rename('x'), band.rename('y')]).updateMask(waterMask);
            const fit = ee.Dictionary(
              stack.reduceRegion({
                reducer: ee.Reducer.linearFit(),
                geometry: roiRef.current,
                scale,
                bestEffort: true,
                tileScale: 8,
              }),
            );
            const slope = ee.Number(fit.get('scale'));
            return band.subtract(nir.subtract(nirMin).multiply(slope)).rename(bandName);
          });

          return image.addBands(correctedBands, null, true);
        });

        logGlintDiagnostics(
          mission,
          missionCollection.first(),
          correctedMissionCollection.first(),
          scale,
          visibleBands,
        );

        return correctedMissionCollection;
      });
      collectionRef.current = processedCollections.reduce((acc, col) => acc.merge(col), ee.ImageCollection([]));
    } catch (e) {
      console.error('applyGlintCorrection error:', e);
      throw e;
    }
  };

  /**
   * @function logGlintDiagnostics
   * @description Fire-and-forget diagnostic that logs, for the first image of a mission's
   * collection, the fitted NIR_min and per-band glint slope, plus the mean reflectance of
   * each visible band over water pixels before vs. after `applyGlintCorrection`. Runs a
   * single `.evaluate()` call so it never blocks the main processing chain — useful to sanity
   * check the correction (e.g. slope should be positive and small; mean reflectance should
   * drop, not increase, after correction).
   * @param {Object} mission - Mission definition (shortname, bands, opticalResolution).
   * @param {ee.Image} originalImage - First image of the mission's collection, before correction.
   * @param {ee.Image} correctedImage - Same image, after `applyGlintCorrection`.
   * @param {number} scale - Pixel scale in meters.
   * @param {Array<string>} visibleBands - Visible band names (blue/green/red) to diagnose.
   * *Called by:
   * @see {applyGlintCorrection}
   * @memberof module:Shared
   * @returns {void}
   */
  const logGlintDiagnostics = (mission, originalImage, correctedImage, scale, visibleBands) => {
    const waterMask = originalImage.select('waterMask_final');
    const nir = originalImage.select(mission.bands.nir);

    const nirMin = ee.Number(
      nir
        .updateMask(waterMask)
        .reduceRegion({ reducer: ee.Reducer.min(), geometry: roiRef.current, scale, bestEffort: true, tileScale: 8 })
        .get(mission.bands.nir),
    );

    const perBand = ee.Dictionary.fromLists(
      visibleBands,
      visibleBands.map((bandName) => {
        const stack = ee.Image.cat([nir.rename('x'), originalImage.select(bandName).rename('y')]).updateMask(waterMask);
        const slope = ee.Number(
          ee
            .Dictionary(
              stack.reduceRegion({
                reducer: ee.Reducer.linearFit(),
                geometry: roiRef.current,
                scale,
                bestEffort: true,
                tileScale: 8,
              }),
            )
            .get('scale'),
        );
        const before = ee.Number(
          originalImage
            .select(bandName)
            .updateMask(waterMask)
            .reduceRegion({
              reducer: ee.Reducer.mean(),
              geometry: roiRef.current,
              scale,
              bestEffort: true,
              tileScale: 8,
            })
            .get(bandName),
        );
        const after = ee.Number(
          correctedImage
            .select(bandName)
            .updateMask(waterMask)
            .reduceRegion({
              reducer: ee.Reducer.mean(),
              geometry: roiRef.current,
              scale,
              bestEffort: true,
              tileScale: 8,
            })
            .get(bandName),
        );
        return ee.Dictionary({ slope, before, after });
      }),
    );

    ee.Dictionary({ nirMin, perBand }).evaluate((result, error) => {
      if (error) {
        console.error(`[applyGlintCorrection] diagnóstico falhou para ${mission.shortname}:`, error);
        return;
      }
      console.log(
        `[applyGlintCorrection] ${mission.shortname} — NIR_min=${result.nirMin?.toFixed(4)} ` +
          '(pixels de água, primeira imagem da coleção)',
      );
      Object.entries(result.perBand).forEach(([bandName, { slope, before, after }]) => {
        const delta = before - after;
        console.log(
          `  ${bandName}: slope=${slope?.toFixed(4)}  média antes=${before?.toFixed(4)}  ` +
            `média depois=${after?.toFixed(4)}  Δ=${delta?.toFixed(4)}`,
        );
      });
    });
  };


  /**
   * @function applySPMMask
   * @description Adds the suspended particulate matter band (`SPM02`) to every image of the current
   * collection, using the mission's own spectral bands.
   * It no longer masks anything. It used to drop every non-Landsat pixel reading 18 or more, on the
   * scale of the previous formulation - a threshold the corrected one does not produce, so it was
   * removing imagery by a number that no longer meant what it said. Suspended matter belongs in the
   * filter that chooses calibration points, not in a mask over the imagery; see
   * {@link spmTrainingFilter}.
   * @param {Object[]} missions - List of mission definitions. (shortname,bands)
   * @returns {void}
   * @throws {Error} Throws an error if the SPM calculation fails.
   */
  const applySPMMask = (missions) => {
    try {
      const processedCollections = missions.map((mission) => {
        const missionCollection =
          satelliteRef.current.name === 'Landsat'
            ? collectionRef.current.filter(ee.Filter.stringContains('system:index', mission.shortname))
            : collectionRef.current;
        return missionCollection.map((image) => addSPM02(image, mission.bands));
      });
      collectionRef.current = processedCollections.reduce((acc, col) => acc.merge(col), ee.ImageCollection([]));
    } catch (e) {
      console.error('applySPMMask error:', e);
      throw e;
    }
  };

  /**
   * @function addTideBand
   * @description Adds a constant tide level band (`tideLevels`) to all images in the current collection.
   * The generated band contains a constant value of `0.5` for every pixel and uses the projection of the mission's blue band.
   * The band is masked to the current region of interest (ROI), see {@link limitToRoi}, and the image keeps its own properties.
   * @param {Object[]} missions - List of mission definitions. (shortname, bands)
   * @returns {void}
   * @throws {Error} Throws an error if the tide band cannot be generated or added.
   */
  const addTideBand = (missions) => {
    try {
      const processedCollections = missions.map((mission) => {
        const missionCollection =
          satelliteRef.current.name === 'Landsat'
            ? collectionRef.current.filter(ee.Filter.stringContains('system:index', mission.shortname))
            : collectionRef.current;
        return missionCollection.map((image) => {
          const tideMask = limitToRoi(
            ee.Image.constant(0.5)
              .rename('tideLevels')
              .reproject(image.select(mission.bands.blue).projection())
              .unmask(0),
          );
          return image.addBands(tideMask, null, true);
        });
      });
      collectionRef.current = processedCollections.reduce((acc, col) => acc.merge(col), ee.ImageCollection([]));
    } catch (e) {
      console.error('addTideBand error:', e);
      throw e;
    }
  };

  return (
    <PreProcessingContext.Provider
      value={{
        saveProcessingCollectionValues,
        calculateCloudCoverageValues,
        collectionRef,
        filterCloudROIByValue,
        generateCollection,
        sharpenCollection,
        satelliteRef,
        roiRef,
        waterMaskMethodRef,
        applyWaterMask,
        applyGlintCorrection,
        applySPMMask,
        addTideBand,
        createMosaicByCommonId,
        applyScaleFactors,
        cleanAndMaskImages,
      }}>
      {children}
    </PreProcessingContext.Provider>
  );
}

export const usePreProcessing = () => useContext(PreProcessingContext);
